import React, { useState, useEffect, useReducer, useRef, useCallback } from "react";
import Navbar from "./NavigationTalent";
import MessagesList from "../MessagesList";
import ReplyDrawer from "../ReplyDrawer";
import GlobalNotification from "../components/GlobalNotification";
import usePolling from "../hooks/useMessagePolling";
import {
  Snackbar,
  Alert,
  Container,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import PaginationControl from "../utils/PaginationControl";
import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";
import Footer from "../Footer";

// How often to silently re-fetch the active tab's messages (ms).
const POLL_INTERVAL_MS = 15000;

const initialState = {
  inbox: {
    messages: [],
    loading: false,
    page: 1,
    hasMore: false,
    totalItems: 0,
  },
  archive: {
    messages: [],
    loading: false,
    page: 1,
    hasMore: false,
    totalItems: 0,
  },
};

function messageReducer(state, action) {
  switch (action.type) {
    case "SET_MESSAGES": {
      const { type, messages, page, hasMore, totalItems } = action.payload;
      return {
        ...state,
        [type]: {
          ...state[type],
          messages,
          page,
          hasMore,
          totalItems,
        },
      };
    }
    case "SET_LOADING": {
      const { type, loading } = action.payload;
      return {
        ...state,
        [type]: { ...state[type], loading },
      };
    }
    default:
      return state;
  }
}

const InboxTalentPage = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [state, dispatch] = useReducer(messageReducer, initialState);
  const [tabIndex, setTabIndex] = useState(0);
  const [replyOpen, setReplyOpen] = useState(false);
  const [messageToReply, setMessageToReply] = useState(null);
  const pageSize = 10;
  const activeTab = tabIndex === 0 ? "inbox" : "archive";
  const [sortOrder, setSortOrder] = useState("Latest");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // --- Auto-refresh bookkeeping ---
  // Refs (not state) so updating them never triggers a re-render or re-runs an effect.
  const hasSelectionRef = useRef(false);     // true while the user has rows checked in MessagesList
  const activeTabRef = useRef(activeTab);    // lets the interval always read the *current* tab/page
  const currentPageRef = useRef(1);
  const sortOrderRef = useRef(sortOrder);

  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { currentPageRef.current = state[activeTab].page; }, [state, activeTab]);
  useEffect(() => { sortOrderRef.current = sortOrder; }, [sortOrder]);

const fetchMessages = async (type, page = 1, sort = sortOrder, { silent = false } = {}) => {
  // "silent" fetches (background polling) skip the loading spinner so the list
  // doesn't flicker/reset scroll position every 15s — only the data swaps in.
  if (!silent) {
    dispatch({ type: "SET_LOADING", payload: { type, loading: true } });
  }
  try {
    const email = localStorage.getItem("email");

    const url = type === "archive"
      ? ENDPOINTS.getArchiveTalentMessages({ email, page, limit: pageSize, sortOrder: sort })
      : ENDPOINTS.getUserTalentMessages({ type, email, page, limit: pageSize, sortOrder: sort });
    const resp = await api.get(url);
    const { totalItems = 0, items: messages = [] } = resp.data?.data || {};

    dispatch({
      type: "SET_MESSAGES",
      payload: { type, messages, page, hasMore: messages.length === pageSize, totalItems },
    });
  } catch (err) {
    console.error(`Error fetching ${type}:`, err);
    // Silent/background failures stay quiet — a dropped poll tick shouldn't
    // interrupt the user; it'll just try again next interval.
  } finally {
    if (!silent) {
      dispatch({ type: "SET_LOADING", payload: { type, loading: false } });
    }
  }
};

  useEffect(() => {
    fetchMessages("inbox", 1);
  }, []);

  
const [userId, setUserId] = useState(null);

const userRole = localStorage.getItem("userRole");

useEffect(() => {
  const id = localStorage.getItem("userId");

  if (id) {
    setUserId(id);
  }
}, []);

usePolling(
  () => {
    fetchMessages(
      activeTabRef.current,
      currentPageRef.current,
      sortOrderRef.current,
      { silent: true }
    );
  },
  {
    interval: POLL_INTERVAL_MS,
    pauseRef: hasSelectionRef,
  }
);

  // Called by MessagesList whenever its internal selection changes
  const handleSelectionChange = useCallback((hasSelection) => {
    hasSelectionRef.current = hasSelection;
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    const type = newValue === 0 ? "inbox" : "archive";
    fetchMessages(type, 1);
  };
const showSnackbar = (message, severity = "success") => {
  setSnackbarMessage(message);
  setSnackbarSeverity(severity);
  setOpenSnackbar(true);
};

  const handleArchive = async (messageIds) => {
    try {
      const payload = {
        originalMsgIds: Array.isArray(messageIds) ? messageIds : [messageIds],
        activeFlag: true,
        archiveFrom: "Talent",
      };

      await api.post(ENDPOINTS.archiveMessages(), payload);

showSnackbar("Messages archived successfully!");
      
      fetchMessages("inbox", 1);
    } catch (error) {
      console.error("Error archiving messages:", error);

showSnackbar("Failed to archive messages. Please try again.", "error");
      
    }
  };

  const handleMoveToInbox = async (messageIds) => {
    try {
      const payload = {
        originalMsgIds: Array.isArray(messageIds) ? messageIds : [messageIds],
        activeFlag: true,
        userId: localStorage.getItem("email"),
      };

      await api.post(ENDPOINTS.archiveMessages(), {
        originalMsgIds: Array.isArray(messageIds) ? messageIds : [messageIds],
        archiveFrom: "Talent",
        activeFlag: false,
      });

showSnackbar("Messages moved to Inbox successfully!");
      
      fetchMessages("archive", 1);
    } catch (error) {
      console.error("Error moving messages to inbox:", error);
  showSnackbar("Failed to move messages to Inbox. Please try again.", "error");
      
    }
  };

const handleDelete = async (messageIds) => {
  try {
    const payload = {
      originalMsgIds: Array.isArray(messageIds) ? messageIds : [messageIds],
      userId: localStorage.getItem("email"),
      activeFlag: true,
      permanentArchive: true,
    };

    await api.post(ENDPOINTS.deleteMessages(), payload);

    showSnackbar("Message(s) deleted successfully!", "error");
    fetchMessages("archive", 1);
  } catch (error) {
    console.error("Error deleting message:", error);

    showSnackbar("Failed to delete message. Please try again.", "error");
  }
};
  return (
    <>
  <Navbar />

  {userRole !== "ADMIN" && userId && (
    <div
          className="shadow"
          style={{ maxWidth: "1200px", margin: "auto", padding: "20px 0" }}
        >
    <GlobalNotification
      userId={userId}
      userRole={userRole}
    />
    </div>
  )}
      <div className="background-prop">
        <div
          className="shadow"
          style={{ maxWidth: "1200px", margin: "auto", padding: "20px" }}
        >
          <Container>
            <Snackbar
              open={openSnackbar}
              autoHideDuration={3000}
              onClose={() => setOpenSnackbar(false)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                onClose={() => setOpenSnackbar(false)}
                severity={snackbarSeverity}
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>

            <Typography sx={{ fontSize: 30, mt: 2, mb: 2, fontWeight: 600 }}>
              Inbox
            </Typography>

            <Tabs value={tabIndex} onChange={handleTabChange}>
              <Tab label="Inbox" />
              <Tab label="Archived" />
            </Tabs>

            <MessagesList
              messages={state[activeTab].messages}
              loading={state[activeTab].loading}
              onSortChange={(value) => {
                setSortOrder(value);
                fetchMessages(activeTab, 1, value);
              }}
              sortOrder={sortOrder}
              onReply={(msg) => {
                setMessageToReply(msg);
                setReplyOpen(true);
              }}
              onArchive={tabIndex === 0 ? handleArchive : undefined}
              onInbox={tabIndex === 1 ? handleMoveToInbox : undefined}
              onDelete={tabIndex === 1 ? handleDelete : undefined}
              tabIndex={tabIndex}
              onSelectionChange={handleSelectionChange}
            />

            <PaginationControl
              page={state[activeTab].page}
              totalItems={state[activeTab].totalItems}
              pageSize={pageSize}
              onPageChange={(newPage) => fetchMessages(activeTab, newPage)}
              sx={{ mt: 3, mb: 5 }}
            />

            <ReplyDrawer
              open={replyOpen}
              onClose={() => setReplyOpen(false)}
              selectedMessage={messageToReply}
            />
          </Container>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default InboxTalentPage;